import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { VfxPhysicsParticleBatch } from "../../core/batches/vfx_physics_particle_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { randomRange } from "../../../common/common_funcs.js";

export class VfxWatermelonBreakTask implements IVfxTask, IController
{
  // --- Watermelon Droplets Constants ---
  private static readonly WATERMELON_COUNT                : number      = 14;
  private static readonly WATERMELON_SPAWN_RADIUS         : number      = 0.45;
  private static readonly WATERMELON_SPAWN_HALF_HEIGHT    : number      = 0.25;
  private static readonly WATERMELON_LIFESPAN             : number      = 0.6;
  private static readonly WATERMELON_LIFESPAN_MIN_MULT    : number      = 0.85;
  private static readonly WATERMELON_LIFESPAN_MAX_MULT    : number      = 1.15;
  private static readonly WATERMELON_MIN_RADIAL_SPEED     : number      = 1;
  private static readonly WATERMELON_MAX_RADIAL_SPEED     : number      = 6;
  private static readonly WATERMELON_MIN_UP_SPEED         : number      = 0;
  private static readonly WATERMELON_MAX_UP_SPEED         : number      = 4;
  private static readonly WATERMELON_MIN_SCALE            : number      = 0.5;
  private static readonly WATERMELON_MAX_SCALE            : number      = 0.7;
  private static readonly WATERMELON_GRAVITY              : number      = 14;
  private static readonly WATERMELON_INITIAL_DISSOLVE_MIN : number      = 0.3;
  private static readonly WATERMELON_INITIAL_DISSOLVE_MAX : number      = 0.4;
  private static readonly WATERMELON_TEXTURE_RES_ID       : ResourceID  = ResourceID.WatermelonJuiceTexture;
  private static readonly WATERMELON_NOISE_RES_ID         : ResourceID  = ResourceID.PerlinNoiseTexture;
  private static readonly WATERMELON_NOISE_SCALE          : number      = 1.0;
  private static readonly WATERMELON_DISSOLVE_START       : number      = 0.1;
  private static readonly WATERMELON_DISSOLVE_SOFTNESS    : number      = 0.1;

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();

  public is_active       : boolean = true;
  private position       : THREE.Vector3;
  private start_time     : number;
  private has_spawned    : boolean = false;
  private total_lifespan : number;

  public constructor( position: THREE.Vector3 )
  {
    this.position       = position.clone();
    this.start_time     = Vfx3DRuntime.current_time;
    this.total_lifespan = VfxWatermelonBreakTask.WATERMELON_LIFESPAN;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnWatermelonSplashes();
    }

    if ( age >= this.total_lifespan )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private spawnWatermelonSplashes(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxPhysicsParticleBatch );
    const count = VfxWatermelonBreakTask.WATERMELON_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = randomRange( 0, Math.PI * 2 );
      const spawn_dist = randomRange( 0, VfxWatermelonBreakTask.WATERMELON_SPAWN_RADIUS );
      const spawn_y = randomRange(
        -VfxWatermelonBreakTask.WATERMELON_SPAWN_HALF_HEIGHT,
        VfxWatermelonBreakTask.WATERMELON_SPAWN_HALF_HEIGHT
      );

      VfxWatermelonBreakTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + spawn_y,
        this.position.z + Math.sin( angle ) * spawn_dist
      );

      const radial_speed = randomRange(
        VfxWatermelonBreakTask.WATERMELON_MIN_RADIAL_SPEED,
        VfxWatermelonBreakTask.WATERMELON_MAX_RADIAL_SPEED
      );
      const up_speed = randomRange(
        VfxWatermelonBreakTask.WATERMELON_MIN_UP_SPEED,
        VfxWatermelonBreakTask.WATERMELON_MAX_UP_SPEED
      );

      VfxWatermelonBreakTask.scratch_vel.set(
        Math.cos( angle ) * radial_speed,
        up_speed,
        Math.sin( angle ) * radial_speed
      );

      const scale = randomRange(
        VfxWatermelonBreakTask.WATERMELON_MIN_SCALE,
        VfxWatermelonBreakTask.WATERMELON_MAX_SCALE
      );

      const initial_dissolve = randomRange(
        VfxWatermelonBreakTask.WATERMELON_INITIAL_DISSOLVE_MIN,
        VfxWatermelonBreakTask.WATERMELON_INITIAL_DISSOLVE_MAX
      );

      batch.spawnParticle(
        VfxWatermelonBreakTask.WATERMELON_TEXTURE_RES_ID,
        VfxWatermelonBreakTask.WATERMELON_NOISE_RES_ID,
        VfxWatermelonBreakTask.scratch_pos,
        VfxWatermelonBreakTask.scratch_vel,
        VfxWatermelonBreakTask.WATERMELON_GRAVITY,
        scale,
        this.start_time,
        VfxWatermelonBreakTask.WATERMELON_LIFESPAN * randomRange(
          VfxWatermelonBreakTask.WATERMELON_LIFESPAN_MIN_MULT,
          VfxWatermelonBreakTask.WATERMELON_LIFESPAN_MAX_MULT
        ),
        initial_dissolve,
        VfxWatermelonBreakTask.WATERMELON_NOISE_SCALE,
        undefined,
        undefined,
        undefined,
        VfxWatermelonBreakTask.WATERMELON_DISSOLVE_START,
        VfxWatermelonBreakTask.WATERMELON_DISSOLVE_SOFTNESS
      );
    }
  }
}
